import { ReactNode } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from '@chakra-ui/react';
import Link from 'next/link';

import { BaseLayout } from 'components/common';
import SeoHead from 'components/common/SeoHead';
import JsonLd from 'components/common/JsonLd';
import { HOME, FONT_HEADING, FONT_BODY } from 'components/home/palette';

type FAQItem = {
  question: string;
  answer: string;
};

type AnswerHubLayoutProps = {
  title: string;
  description: string;
  slug: string;
  faqs: FAQItem[];
  children: ReactNode;
};

export const AnswerHubLayout = ({
  title,
  description,
  slug,
  faqs,
  children,
}: AnswerHubLayoutProps) => {
  // Generate the FAQPage schema dynamically from the FAQ items
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SeoHead
        title={title}
        description={description}
        path={`/${slug}`}
      />
      <JsonLd schema={faqSchema} />
      <BaseLayout>
        <Box as="main" bg={HOME.bgSoft} py={{ base: 8, md: 12 }} px={4}>
          <Container maxW="800px">
            {/* Breadcrumb Navigation */}
            <Breadcrumb
              fontSize="sm"
              fontFamily={FONT_BODY}
              color={HOME.muted}
              mb={6}
            >
              <BreadcrumbItem>
                <Link href="/" passHref>
                  <BreadcrumbLink _hover={{ color: HOME.teal }}>Home</BreadcrumbLink>
                </Link>
              </BreadcrumbItem>
              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink color={HOME.ink} fontWeight={600}>
                  {title}
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>

            {/* Article Wrapper Card */}
            <Box
              bg="white"
              borderRadius="24px"
              border={`1px solid ${HOME.border}`}
              boxShadow="0 10px 30px rgba(30,61,96,0.04)"
              p={{ base: 6, md: 10 }}
            >
              {/* Category Header Tag */}
              <Box display="inline-flex" alignItems="center" gap={2} mb={4}>
                <Box w="8px" h="8px" borderRadius="full" bg={HOME.teal} />
                <Text
                  fontFamily={FONT_BODY}
                  fontSize="xs"
                  fontWeight={700}
                  letterSpacing="0.1em"
                  textTransform="uppercase"
                  color={HOME.teal}
                >
                  UK Tutoring Guide
                </Text>
              </Box>

              {/* Title */}
              <Heading
                as="h1"
                fontFamily={FONT_HEADING}
                fontSize={{ base: '2xl', md: '4xl' }}
                fontWeight={700}
                lineHeight="1.2"
                color={HOME.ink}
                mb={6}
              >
                {title}
              </Heading>

              <Divider mb={8} borderColor={HOME.border} />

              {/* Rich Body Content */}
              <Box
                fontFamily={FONT_BODY}
                fontSize="md"
                lineHeight="1.8"
                color="#2d3a4a"
                sx={{
                  'p': {
                    mb: 5,
                  },
                  'h2': {
                    fontSize: 'xl',
                    fontWeight: 700,
                    color: HOME.ink,
                    mt: 10,
                    mb: 4,
                    fontFamily: FONT_HEADING,
                  },
                  'h3': {
                    fontSize: 'lg',
                    fontWeight: 700,
                    color: HOME.ink,
                    mt: 8,
                    mb: 3,
                    fontFamily: FONT_HEADING,
                  },
                  'ul, ol': {
                    pl: 6,
                    mb: 5,
                  },
                  'li': {
                    mb: 2,
                  },
                  'a': {
                    color: HOME.teal,
                    fontWeight: 600,
                    textDecoration: 'underline',
                    _hover: { color: HOME.tealDeep },
                  },
                  'table': {
                    width: '100%',
                    borderCollapse: 'collapse',
                    my: 6,
                    fontSize: 'sm',
                  },
                  'th': {
                    bg: HOME.ink,
                    color: 'white',
                    fontWeight: 600,
                    textAlign: 'left',
                    p: 3,
                    border: `1px solid ${HOME.border}`,
                  },
                  'td': {
                    p: 3,
                    border: `1px solid ${HOME.border}`,
                  },
                  'tr:nth-of-type(even)': {
                    bg: HOME.bgSoft,
                  },
                }}
              >
                {children}
              </Box>

              {/* FAQ Accordion Section */}
              {faqs.length > 0 && (
                <Box mt={12} pt={8} borderTop={`1px solid ${HOME.border}`}>
                  <Heading
                    as="h2"
                    fontFamily={FONT_HEADING}
                    fontSize="2xl"
                    fontWeight={700}
                    color={HOME.ink}
                    mb={6}
                  >
                    Frequently Asked Questions
                  </Heading>
                  <Accordion allowMultiple>
                    {faqs.map((faq, idx) => (
                      <AccordionItem key={idx} borderTop="none" borderBottom={`1px solid ${HOME.border}`} py={2}>
                        <h3>
                          <AccordionButton _hover={{ bg: HOME.bgSoft }} borderRadius="8px" py={3}>
                            <Box flex="1" textAlign="left" fontWeight={600} color={HOME.ink} fontFamily={FONT_HEADING}>
                              {faq.question}
                            </Box>
                            <AccordionIcon color={HOME.teal} />
                          </AccordionButton>
                        </h3>
                        <AccordionPanel pb={4} pt={2} color="#4a5568" pl={4} lineHeight="1.7">
                          {faq.answer}
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </Box>
              )}
            </Box>
          </Container>
        </Box>
      </BaseLayout>
    </>
  );
}
